// How a setting's value is shown. A ratio stored 0..1 with the unit "%" is
// shown as a percentage (0.15 → 15) and stored back as the ratio; everything
// else is shown as stored.

import type { ResolvedSetting } from '../types';

/** Clears float noise: 0.15 × 100 is 15.000000000000002. */
const clean = (v: number) => Number(v.toFixed(10));

/** A ratio shown as a percentage: a number with the unit "%" whose range is 0..1. */
export function isPercent(s: Pick<ResolvedSetting, 'type' | 'maximum' | 'ui'>): boolean {
  return s.type === 'number' && s.ui.unit === '%' && (s.maximum == null || s.maximum <= 1);
}

export function toDisplay(s: Pick<ResolvedSetting, 'type' | 'maximum' | 'ui'>, value: number): number {
  return isPercent(s) ? clean(value * 100) : value;
}

export function fromDisplay(s: Pick<ResolvedSetting, 'type' | 'maximum' | 'ui'>, shown: number): number {
  return isPercent(s) ? clean(shown / 100) : shown;
}

/** What the person typed, as a stored value; undefined when it is not a number. */
export function parseDisplay(s: Pick<ResolvedSetting, 'type' | 'maximum' | 'ui'>, text: string): number | undefined {
  const t = text.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? fromDisplay(s, n) : undefined;
}

/** The bounds and step of the control, in what the person sees. */
export function displayRange(s: ResolvedSetting): { min?: number; max?: number; step: number } {
  const min = s.minimum != null ? toDisplay(s, s.minimum) : undefined;
  const max = s.maximum != null ? toDisplay(s, s.maximum) : undefined;
  let step = 1;
  if (s.type === 'integer' || isPercent(s)) step = 1;
  else if (min != null && max != null && max > min) step = niceStep((max - min) / 100);
  return { min, max, step };
}

/** 1, 2 or 5 times a power of ten, at most `raw`. */
function niceStep(raw: number): number {
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return clean((m >= 5 ? 5 : m >= 2 ? 2 : 1) * p);
}

/** The unit written after the control: "%" for a percentage, else the action's unit. */
export function unitLabel(s: ResolvedSetting): string | null {
  return s.ui.unit?.trim() || null;
}

/** A value in words, for read-only lines and messages: "15%", "0.5", "On". */
export function formatSettingValue(s: ResolvedSetting, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'number') {
    const shown = toDisplay(s, value);
    const unit = unitLabel(s);
    return unit === '%' ? `${shown}%` : unit ? `${shown} ${unit}` : String(shown);
  }
  if (value == null) return '';
  return Array.isArray(s.enum) ? optionLabel(value) : String(value);
}

/** An option of a select: short codes in capitals ("ihc" → "IHC"), else the first letter capitalised. */
export function optionLabel(value: unknown): string {
  const text = String(value);
  if (/^[a-z]{2,3}$/.test(text)) return text.toUpperCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).replace(/[_-]+/g, ' ') : text;
}
